export type AnalysisErrorCodeEnum =
  | 'TASK_FAILED'
  | 'GEOCUT_FAILED'
  | 'GEOCUT_NO_2D_IMAGE_FOUND'
  | 'GEOCUT_REQUIRED_3D_IMAGE_NOT_FOUND'
  | 'GEOCUT_INVALID_ANALYSIS_DATA'
  | 'GEOCUT_ADDRESS_NOT_GEOCODED'
  | 'GEOCUT_RAW_FILES_RESPONSE_INVALID'
  | 'GEOCUT_GSD_MISSING_2D'
  | 'GEOCUT_GSD_MISSING_3D'
  | 'GEOCUT_UPLOAD_URL_MISSING'
  | 'GEOCUT_UPLOAD_FAILED'
  | 'GEOCUT_EXTERNAL_REQUEST_FAILED'
  | 'GEOCUT_ACCESS_DENIED'
  | 'GEOCUT_REQUEST_REJECTED'
  | 'TASK_DELIVERY_LIMIT_EXCEEDED'
  | 'DETECTION_FAILED'
  | 'DETECTION_NO_IMAGE'
  | 'DETECTION_MODEL_NOT_FOUND'
  | 'ORIENTATION_FAILED'
  | 'NO_ROOF_AREAS'
  | 'MAX_PLACEMENT_FAILED'
  | 'MAX_PLACEMENT_NO_ROOF_AREAS'
  | 'PLANNING_FAILED'
  | 'PLANNING_ROOF_TOO_SMALL'
  | 'PLANNING_NO_ROOF_POLYGONS'
  | 'PLANNING_INVALID_POLYGON_DATA'
  | 'EXPORT_FAILED'
  | 'PIPELINE_FAILED';

export interface ErrorCodeMetadata {
  code: string;
  title: string;
  description: string;
  icon: 'error' | 'warning' | 'info';
  retryable: boolean;
}

const ERROR_CODE_METADATA: Record<string, ErrorCodeMetadata> = {
  GEOCUT_ACCESS_DENIED: {
    code: 'GEOCUT_ACCESS_DENIED',
    title: 'Geodatenzugriff verweigert',
    description: 'Ein benötigter Dienst verweigert den Zugriff. Bitte technische Zugangsdaten und Berechtigungen prüfen, bevor erneut gestartet wird.',
    icon: 'error',
    retryable: false,
  },
  GEOCUT_REQUEST_REJECTED: {
    code: 'GEOCUT_REQUEST_REJECTED',
    title: 'Geodatenanfrage abgelehnt',
    description: 'Bitte die Anfrage und Dienstkonfiguration prüfen, bevor erneut gestartet wird.',
    icon: 'error',
    retryable: false,
  },
  TASK_DELIVERY_LIMIT_EXCEEDED: {
    code: 'TASK_DELIVERY_LIMIT_EXCEEDED',
    title: 'Zustellungslimit erreicht',
    description: 'Der Verarbeitungsschritt wurde zu oft zugestellt. Die Ursache muss anhand der Worker- und API-Logs geprüft werden; ein Zeitablauf oder Worker-Absturz ist damit nicht nachgewiesen.',
    icon: 'error',
    retryable: false,
  },
  TASK_FAILED: {
    code: 'TASK_FAILED',
    title: 'Verarbeitung fehlgeschlagen',
    description: 'Ein Verarbeitungsschritt ist fehlgeschlagen.',
    icon: 'error',
    retryable: true,
  },
  GEOCUT_FAILED: {
    code: 'GEOCUT_FAILED',
    title: 'Bildzuschnitt fehlgeschlagen',
    description: 'Der Geodaten-Zuschnitt konnte nicht abgeschlossen werden.',
    icon: 'error',
    retryable: false,
  },
  GEOCUT_NO_2D_IMAGE_FOUND: {
    code: 'GEOCUT_NO_2D_IMAGE_FOUND',
    title: 'Kein passendes 2D-Bild gefunden',
    description: 'Für die Adresse wurde kein passendes 2D-Quellbild gefunden.',
    icon: 'warning',
    retryable: false,
  },
  GEOCUT_REQUIRED_3D_IMAGE_NOT_FOUND: {
    code: 'GEOCUT_REQUIRED_3D_IMAGE_NOT_FOUND',
    title: '3D-Quelle fehlt',
    description: 'Eine erforderliche 3D-Datei wurde nicht gefunden.',
    icon: 'warning',
    retryable: false,
  },
  GEOCUT_INVALID_ANALYSIS_DATA: {
    code: 'GEOCUT_INVALID_ANALYSIS_DATA',
    title: 'Unvollständige Analysedaten',
    description: 'Notwendige Analysedaten fehlen.',
    icon: 'warning',
    retryable: false,
  },
  GEOCUT_ADDRESS_NOT_GEOCODED: {
    code: 'GEOCUT_ADDRESS_NOT_GEOCODED',
    title: 'Adresse nicht geokodiert',
    description: 'Zur Adresse fehlen gültige Koordinaten.',
    icon: 'warning',
    retryable: false,
  },
  GEOCUT_RAW_FILES_RESPONSE_INVALID: {
    code: 'GEOCUT_RAW_FILES_RESPONSE_INVALID',
    title: 'Dateidaten ungültig',
    description: 'Die Quell-Dateiliste ist ungültig oder unvollständig.',
    icon: 'warning',
    retryable: false,
  },
  GEOCUT_GSD_MISSING_2D: {
    code: 'GEOCUT_GSD_MISSING_2D',
    title: '2D-Metadaten fehlen',
    description: 'Für das 2D-Bild fehlen notwendige Auflösungsdaten.',
    icon: 'warning',
    retryable: false,
  },
  GEOCUT_GSD_MISSING_3D: {
    code: 'GEOCUT_GSD_MISSING_3D',
    title: '3D-Metadaten fehlen',
    description: 'Für das 3D-Modell fehlen notwendige Auflösungsdaten.',
    icon: 'warning',
    retryable: false,
  },
  GEOCUT_UPLOAD_URL_MISSING: {
    code: 'GEOCUT_UPLOAD_URL_MISSING',
    title: 'Upload-Link fehlt',
    description: 'Ein erforderlicher Upload-Link konnte nicht erzeugt werden.',
    icon: 'error',
    retryable: true,
  },
  GEOCUT_UPLOAD_FAILED: {
    code: 'GEOCUT_UPLOAD_FAILED',
    title: 'Upload fehlgeschlagen',
    description: 'Ein Datei-Upload ist fehlgeschlagen.',
    icon: 'error',
    retryable: true,
  },
  GEOCUT_EXTERNAL_REQUEST_FAILED: {
    code: 'GEOCUT_EXTERNAL_REQUEST_FAILED',
    title: 'Externer Dienst nicht erreichbar',
    description: 'Ein benötigter Dienst konnte nicht erreicht werden.',
    icon: 'error',
    retryable: true,
  },
  DETECTION_FAILED: {
    code: 'DETECTION_FAILED',
    title: 'Erkennung fehlgeschlagen',
    description: 'Die Objekterkennung konnte nicht abgeschlossen werden.',
    icon: 'error',
    retryable: true,
  },
  DETECTION_NO_IMAGE: {
    code: 'DETECTION_NO_IMAGE',
    title: 'Satellitenbild fehlt',
    description:
      'Kein Satellitenbild für diese Analyse gefunden. Bitte zuerst den GeoCut-Schritt ausführen.',
    icon: 'warning',
    retryable: false,
  },
  DETECTION_MODEL_NOT_FOUND: {
    code: 'DETECTION_MODEL_NOT_FOUND',
    title: 'KI-Modell nicht gefunden',
    description:
      'Ein benötigtes KI-Erkennungsmodell fehlt auf dem Server. Bitte den Administrator kontaktieren.',
    icon: 'error',
    retryable: false,
  },
  ORIENTATION_FAILED: {
    code: 'ORIENTATION_FAILED',
    title: 'Ausrichtung fehlgeschlagen',
    description: 'Die Ausrichtungsanalyse ist fehlgeschlagen.',
    icon: 'error',
    retryable: true,
  },
  NO_ROOF_AREAS: {
    code: 'NO_ROOF_AREAS',
    title: 'Keine Dachfläche erkannt',
    description: 'Keine Dachfläche erkannt – bitte prüfen oder einzeichnen.',
    icon: 'warning',
    retryable: false,
  },
  MAX_PLACEMENT_FAILED: {
    code: 'MAX_PLACEMENT_FAILED',
    title: 'Belegungsberechnung fehlgeschlagen',
    description: 'Die Maximalbelegung konnte nicht berechnet werden.',
    icon: 'error',
    retryable: true,
  },
  MAX_PLACEMENT_NO_ROOF_AREAS: {
    code: 'MAX_PLACEMENT_NO_ROOF_AREAS',
    title: 'Keine Dachflächen vorhanden',
    description:
      'Für diese Analyse wurden keine Dachflächen gefunden. Bitte zuerst die Erkennungs-Stage ausführen.',
    icon: 'warning',
    retryable: false,
  },
  PLANNING_FAILED: {
    code: 'PLANNING_FAILED',
    title: 'Planung fehlgeschlagen',
    description: 'Die Anlagenplanung ist fehlgeschlagen.',
    icon: 'error',
    retryable: true,
  },
  PLANNING_ROOF_TOO_SMALL: {
    code: 'PLANNING_ROOF_TOO_SMALL',
    title: 'Dachfläche zu klein',
    description:
      'Auf keiner Dachfläche ist Platz für die Mindestanzahl an Modulen (4 je Schrägdachfläche, 2 je Flachdachfläche). Bitte die nutzbaren Dachflächen prüfen.',
    icon: 'warning',
    retryable: false,
  },
  PLANNING_NO_ROOF_POLYGONS: {
    code: 'PLANNING_NO_ROOF_POLYGONS',
    title: 'Keine Dachflächen für die Planung',
    description:
      'Es sind keine auswertbaren Dachflächen vorhanden. Bitte zuerst die Erkennungs-Stage ausführen.',
    icon: 'warning',
    retryable: false,
  },
  PLANNING_INVALID_POLYGON_DATA: {
    code: 'PLANNING_INVALID_POLYGON_DATA',
    title: 'Ungültige Dachflächendaten',
    description:
      'Die Polygondaten einer oder mehrerer Dachflächen sind fehlerhaft. Bitte die Dachflächen prüfen und erneut erkennen lassen.',
    icon: 'warning',
    retryable: false,
  },
  EXPORT_FAILED: {
    code: 'EXPORT_FAILED',
    title: 'Export fehlgeschlagen',
    description: 'Der Ergebnisexport konnte nicht erstellt werden.',
    icon: 'error',
    retryable: true,
  },
  PIPELINE_FAILED: {
    code: 'PIPELINE_FAILED',
    title: 'Pipeline fehlgeschlagen',
    description: 'Die Gesamtpipeline wurde abgebrochen.',
    icon: 'error',
    retryable: true,
  },
};

const UNKNOWN_ERROR_METADATA: ErrorCodeMetadata = {
  code: 'UNKNOWN_ERROR',
  title: 'Unbekannter Fehler',
  description:
    'Die Verarbeitung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
  icon: 'error',
  retryable: true,
};

export function getErrorCodeMetadata(code: string): ErrorCodeMetadata {
  return (
    ERROR_CODE_METADATA[code] ?? {
      ...UNKNOWN_ERROR_METADATA,
      code,
    }
  );
}

export function isErrorRetryable(code: string): boolean {
  return getErrorCodeMetadata(code).retryable;
}

export function getAnalysisErrorMessage(
  code: string | null | undefined,
  message: string | null | undefined,
  detail?: string | null,
): string | undefined {
  // Older Hub snapshots contain an unsupported OOM diagnosis for this code.
  // Correct the presentation without rewriting the historical failure record.
  const explanation = code === 'TASK_DELIVERY_LIMIT_EXCEEDED'
    ? ERROR_CODE_METADATA[code].description
    : message || (code ? ERROR_CODE_METADATA[code]?.description : undefined);
  return [explanation, detail].filter(Boolean).join(' — ') || undefined;
}

/**
 * The planning worker reports failures as English sentences without a code.
 * Map the known ones to the German texts above; anything else stays as sent.
 */
const PLANNER_MESSAGES: [RegExp, string][] = [
  [/^No roof area has room for the minimum number of modules/, ERROR_CODE_METADATA['PLANNING_ROOF_TOO_SMALL'].description],
  [/^At least one roof area is required/, ERROR_CODE_METADATA['PLANNING_NO_ROOF_POLYGONS'].description],
  [/^Template must define at least one storage option/, 'Die Vorlage enthält keinen Speicher. Bitte in der Vorlage mindestens einen Speicher hinterlegen.'],
  [/^No panel combination could be evaluated/, ERROR_CODE_METADATA['PLANNING_FAILED'].description],
];

export function plannerMessage(message: string | null | undefined): string | null {
  if (!message) return null;
  return PLANNER_MESSAGES.find(([pattern]) => pattern.test(message))?.[1] ?? message;
}
